import { createSelector, createSlice } from '@reduxjs/toolkit'
import { jwtDecode, JwtPayload } from 'jwt-decode'
import dayjs from 'dayjs'

import type { RootState } from '../store'
import { getCustomService } from '../../helpers'
import { User } from '../../rest/types/User'
import { LoginParams } from '../../rest/requests/auth/login'

import { actionTypes } from './types'

//
// requests
//

export const loginService = getCustomService<'login', User, LoginParams>(
  'login'
)

//
// Initial state
//

export type AuthState = {
  user: User | null
  token: string | null
  impersonate?: boolean
  meIsLoading?: boolean
  // requests
  login: typeof loginService.state
}

const initialState: AuthState = {
  user: null,
  token: null,
  impersonate: false,
  meIsLoading: false,
  // requests
  login: loginService.state,
}

//
// Slice (Actions & Reducers)
//

const slice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    resetAuth: () => initialState,
    setUser: (state, { payload }: actionTypes.setUser) => {
      state.user = payload
    },
    setToken: (state, action: actionTypes.setToken) => {
      state.token = action.payload
    },
    setImpersonate: (state, action: actionTypes.setImpersonate) => {
      state.impersonate = action.payload
    },
    logout: () => undefined,
    meIsLoading: (state, action: actionTypes.setMeIsLoading) => {
      state.meIsLoading = action.payload
    },
    me: () => undefined,
    ...loginService.reducers,
  },
})

export const { reducer, actions } = slice

//
// Selectors
//

const root = (state: RootState) => state[slice.name]
const user = (state: RootState) => root(state).user
const impersonate = (state: RootState) => root(state).impersonate
const token = (state: RootState) => root(state).token
const meIsLoading = (state: RootState) => root(state).meIsLoading
const jwt = createSelector([token], (tokenString): JwtPayload | null => {
  return tokenString ? jwtDecode<JwtPayload>(tokenString) : null
})
const isConnected = createSelector([token], (t): boolean => {
  return !!t && t !== ''
})
const isSubscriptionActive = createSelector([isConnected, user], (c, u) => {
  return (
    c &&
    u?.subscriptionExpiration &&
    dayjs().isBefore(new Date(u?.subscriptionExpiration))
  )
})
const login = (state: RootState) => root(state).login

export const selectors = {
  user,
  impersonate,
  token,
  jwt,
  isConnected,
  isSubscriptionActive,
  login,
  meIsLoading,
}
